/**
 * Player-centric KoT match rows for match-history dialog.
 */

export function isPlayerInKotMatch(match, playerId) {
  if (!match || !playerId) return false;
  const pid = String(playerId);
  return (
    String(match.player_a_id) === pid || String(match.player_b_id) === pid
  );
}

export function formatKotPlayerDisplayName(row) {
  if (!row) return "—";
  return (
    row.user_alias ||
    [row.user_first_name, row.user_last_name].filter(Boolean).join(" ").trim() ||
    (row.player_id ? String(row.player_id).slice(-6) : "—")
  );
}

function opponentIdForMatch(match, playerId) {
  const pid = String(playerId);
  if (String(match.player_a_id) === pid) return match.player_b_id;
  if (String(match.player_b_id) === pid) return match.player_a_id;
  return null;
}

function playerScoreInMatch(match, playerId) {
  const pid = String(playerId);
  if (String(match.player_a_id) === pid) return match.player_a_score;
  if (String(match.player_b_id) === pid) return match.player_b_score;
  return null;
}

function opponentScoreInMatch(match, playerId) {
  const pid = String(playerId);
  if (String(match.player_a_id) === pid) return match.player_b_score;
  if (String(match.player_b_id) === pid) return match.player_a_score;
  return null;
}

/**
 * @returns {"win"|"loss"|"pending"|"abandoned"|"unknown"}
 */
export function kotPlayerMatchOutcome(match, playerId) {
  if (!match) return "unknown";
  const state = match.match_state;
  if (state === "ABANDONED") return "abandoned";
  if (state !== "WON") return "pending";
  const pid = String(playerId);
  if (!match.winner_player_id) return "unknown";
  return String(match.winner_player_id) === pid ? "win" : "loss";
}

export function pointsEarnedInMatch(match, playerId) {
  if (!match || match.match_state !== "WON") return null;
  const pid = String(playerId);
  if (String(match.winner_player_id) === pid) {
    const pts = Number(match.points_awarded_winner);
    return Number.isFinite(pts) ? pts : 0;
  }
  const pts = Number(match.points_awarded_loser);
  return Number.isFinite(pts) ? pts : 0;
}

function opponentPlayerFromMatch(match, playerId) {
  const oppId = opponentIdForMatch(match, playerId);
  if (!oppId) return null;
  const pid = String(oppId);
  if (String(match.player_a_id) === pid && match.player_a) return match.player_a;
  if (String(match.player_b_id) === pid && match.player_b) return match.player_b;
  return null;
}

export function formatClubRankingLabel(classement, rankingPoints) {
  const level = classement ? String(classement).trim() : "";
  const pts =
    rankingPoints != null && Number.isFinite(Number(rankingPoints))
      ? Number(rankingPoints)
      : null;
  if (level && pts != null) return `${level} - ${pts} pts`;
  if (level) return level;
  if (pts != null) return `${pts} pts`;
  return null;
}

/**
 * Build one display row from a raw match + player id.
 * @param {object} match
 * @param {string} playerId
 * @param {(id: string) => string} resolveOpponentName
 */
export function buildKotPlayerMatchRow(match, playerId, resolveOpponentName) {
  const oppId = opponentIdForMatch(match, playerId);
  const myScore = playerScoreInMatch(match, playerId);
  const oppScore = opponentScoreInMatch(match, playerId);
  const scoreLabel =
    myScore != null && oppScore != null
      ? `${myScore} – ${oppScore}`
      : myScore != null || oppScore != null
        ? `${myScore ?? "—"} – ${oppScore ?? "—"}`
        : "—";

  const opp = opponentPlayerFromMatch(match, playerId);
  const outcome = kotPlayerMatchOutcome(match, playerId);
  const points = pointsEarnedInMatch(match, playerId);

  return {
    kot_match_id: match.kot_match_id,
    round_number:
      match.round_number ??
      match.tbl_kot_round?.round_number ??
      null,
    table_number: match.table_number ?? null,
    match_state: match.match_state,
    outcome,
    opponent_name: oppId
      ? resolveOpponentName(String(oppId), match)
      : "—",
    opponent_classement: opp?.fltt_classement || null,
    opponent_ranking_points:
      opp?.fltt_ranking_points != null ? Number(opp.fltt_ranking_points) : null,
    opponent_ranking_label: formatClubRankingLabel(
      opp?.fltt_classement,
      opp?.fltt_ranking_points
    ),
    score_label: scoreLabel,
    points: outcome === "loss" ? 0 : points,
    points_is_loss: outcome === "loss",
    decided_by: match.decided_by || null,
    abandoned_reason: match.abandoned_reason || null,
    createdAt: match.createdAt || null,
  };
}

export function sortKotPlayerMatchRows(rows) {
  return [...rows].sort((a, b) => {
    const ra = Number(a.round_number) || 0;
    const rb = Number(b.round_number) || 0;
    if (ra !== rb) return ra - rb;
    const ta = Number(a.table_number) || 0;
    const tb = Number(b.table_number) || 0;
    if (ta !== tb) return ta - tb;
    const ca = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const cb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (ca !== cb) return ca - cb;
    const idA = String(a.kot_match_id || "");
    const idB = String(b.kot_match_id || "");
    return idA.localeCompare(idB);
  });
}

export function resolveOpponentNameFromMatch(match, opponentId, nameByPlayerId) {
  if (!opponentId) return "—";
  const fromMap = nameByPlayerId?.get(String(opponentId));
  if (fromMap) return fromMap;
  const pid = String(opponentId);
  if (String(match.player_a_id) === pid && match.player_a) {
    return formatKotPlayerDisplayName(match.player_a);
  }
  if (String(match.player_b_id) === pid && match.player_b) {
    return formatKotPlayerDisplayName(match.player_b);
  }
  return String(opponentId).slice(-6);
}

/** Mirrors iOS KoTMatchVideoRetentionStore.sanitizedMatchIdFragment */
export function sanitizedKotMatchIdFragment(matchId) {
  const t = String(matchId || "").replace(/[^a-zA-Z0-9_]/g, "");
  return t ? t.slice(0, 48) : "match";
}

export function toAbsoluteMediaUrl(url, baseUrl) {
  if (!url) return null;
  const s = String(url).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  const base = String(baseUrl || "").replace(/\/$/, "");
  return `${base}/${s.replace(/^\//, "")}`;
}

/** True when a KoT match row has a non-empty resolved media URL to open. */
export function isPlayableKotMatchVideoUrl(url) {
  if (url == null) return false;
  const s = String(url).trim();
  return Boolean(s && s !== "null" && s !== "undefined");
}

/**
 * Resolve uploaded evaluation/feedback videos to KoT matches.
 * iOS stores clips as `training_exercise_{teId}_kot_{matchFragment}_*.mp4`.
 */
export function parseKotMatchIdFromFeedbackText(text) {
  const raw = String(text || "").trim();
  const m = raw.match(/^kot_match_id:(.+)$/i);
  return m ? m[1].trim() : null;
}

/**
 * Resolve which KoT match a feedback clip belongs to (text tag, then filename).
 */
export function resolveKotMatchForFeedback(feedback, matches = []) {
  if (!feedback || !matches?.length) return null;
  const byId = new Map(
    matches
      .filter((m) => m?.kot_match_id)
      .map((m) => [String(m.kot_match_id), m])
  );

  const fromText = parseKotMatchIdFromFeedbackText(feedback.text_message);
  if (fromText && byId.has(fromText)) return byId.get(fromText);

  const haystack = String(feedback.video_message_url || "").toLowerCase();
  if (!haystack.includes("_kot_")) return null;

  for (const match of matches) {
    const id = String(match?.kot_match_id || "");
    if (!id) continue;
    const frag = sanitizedKotMatchIdFragment(id).toLowerCase();
    if (!frag) continue;
    if (haystack.includes(`_kot_${frag}_`)) return match;
  }
  return null;
}

/** Find the feedback row linked to a KoT match (for view_setup / message_id). */
export function findFeedbackForKotMatch(feedbacks, kotMatchId) {
  if (!kotMatchId || !Array.isArray(feedbacks) || !feedbacks.length) return null;
  const id = String(kotMatchId);
  const byText = feedbacks.find(
    (fb) => parseKotMatchIdFromFeedbackText(fb?.text_message) === id
  );
  if (byText) return byText;

  const frag = sanitizedKotMatchIdFragment(id).toLowerCase();
  if (!frag) return null;
  const needle = `_kot_${frag}_`;
  return (
    feedbacks.find((fb) =>
      String(fb?.video_message_url || "")
        .toLowerCase()
        .includes(needle)
    ) || null
  );
}

/**
 * Media-card role lines for a KoT match (A / B players).
 */
export function buildKotMatchVideoRoleLines(
  match,
  viewerIdentityIds = [],
  t = (key, fallback) => fallback || key
) {
  if (!match) return [];
  const ids = (viewerIdentityIds || []).map(String);
  const isViewer = (player) => {
    if (!player || !ids.length) return false;
    return ids.some(
      (id) =>
        (player.player_id != null && String(player.player_id) === id) ||
        (player.user_id != null && String(player.user_id) === id)
    );
  };

  const lines = [];
  if (match.player_a_id || match.player_a) {
    const name = formatKotPlayerDisplayName(match.player_a);
    lines.push({
      slot: "A",
      name:
        name !== "—"
          ? name
          : match.player_a_id
            ? String(match.player_a_id).slice(-6)
            : t("unknownPlayer", "Unknown player"),
      role: null,
      isViewer: isViewer(match.player_a) ||
        (match.player_a_id != null && ids.includes(String(match.player_a_id))),
      isActive: true,
      isSparring: false,
    });
  }
  if (match.player_b_id || match.player_b) {
    const name = formatKotPlayerDisplayName(match.player_b);
    lines.push({
      slot: "B",
      name:
        name !== "—"
          ? name
          : match.player_b_id
            ? String(match.player_b_id).slice(-6)
            : t("unknownPlayer", "Unknown player"),
      role: null,
      isViewer: isViewer(match.player_b) ||
        (match.player_b_id != null && ids.includes(String(match.player_b_id))),
      isActive: true,
      isSparring: false,
    });
  }
  return lines;
}

/** Stable key for deduplicating feedback rows that point at the same clip. */
export function feedbackVideoDedupeKey(url, text, { messageType, senderId } = {}) {
  const kotMatchId = parseKotMatchIdFromFeedbackText(text);
  if (kotMatchId) return `kot:${kotMatchId.toLowerCase()}`;

  if (!url) return "";
  const raw = String(url).trim().split("?")[0].toLowerCase();
  if (!raw) return "";

  let pathKey = raw.replace(/^\/+/, "");
  try {
    if (/^https?:\/\//i.test(raw)) {
      const parsed = new URL(raw);
      pathKey = parsed.pathname.replace(/^\/+/, "");
    }
  } catch {
    // fall through
  }

  if (
    String(messageType || "").toLowerCase() === "evaluation" &&
    senderId != null &&
    String(senderId).trim()
  ) {
    return `eval:${pathKey}:sender:${String(senderId).trim()}`;
  }

  return `url:${pathKey}`;
}

function feedbackVideoDedupeRank(msg, preferredTeId) {
  let score = 0;
  if (preferredTeId && String(msg?.te_id) === String(preferredTeId)) score += 1000;
  if (Number(msg?.sequence_number) > 0) score += 100;
  const created = msg?.createdAt ? new Date(msg.createdAt).getTime() : 0;
  return score + created / 1e15;
}

/** Remove duplicate evaluation/feedback video rows (same URL or KoT match id). */
export function dedupeFeedbackVideoMessages(messages, { preferredTeId } = {}) {
  if (!Array.isArray(messages) || messages.length < 2) return messages || [];

  const bestByKey = new Map();
  for (const msg of messages) {
    if (!msg?.video_message_url) continue;
    const key = feedbackVideoDedupeKey(msg.video_message_url, msg.text_message, {
      messageType: msg.message_type,
      senderId: msg.sender_id,
    });
    if (!key) continue;
    const existing = bestByKey.get(key);
    if (
      !existing ||
      feedbackVideoDedupeRank(msg, preferredTeId) >
        feedbackVideoDedupeRank(existing, preferredTeId)
    ) {
      bestByKey.set(key, msg);
    }
  }

  if (!bestByKey.size) return messages;

  const seenKeys = new Set();
  const result = [];
  for (const msg of messages) {
    if (!msg?.video_message_url) {
      result.push(msg);
      continue;
    }
    const key = feedbackVideoDedupeKey(msg.video_message_url, msg.text_message, {
      messageType: msg.message_type,
      senderId: msg.sender_id,
    });
    if (!key) {
      result.push(msg);
      continue;
    }
    if (bestByKey.get(key) !== msg || seenKeys.has(key)) continue;
    seenKeys.add(key);
    result.push(msg);
  }
  return result;
}

export function buildKotMatchVideoUrlMap(feedbacks, matchIds, baseUrl, options = {}) {
  const map = new Map();
  if (!Array.isArray(feedbacks) || !matchIds?.length) return map;

  const preferredTeId =
    options.teId != null && String(options.teId).trim()
      ? String(options.teId).trim()
      : null;
  const matchIdSet = new Set(matchIds.map((id) => String(id)));
  const fragments = matchIds.map((id) => ({
    id: String(id),
    frag: sanitizedKotMatchIdFragment(id).toLowerCase(),
  }));

  const sortedFeedbacks = [...feedbacks].sort((a, b) => {
    const ta = new Date(a?.createdAt || 0).getTime();
    const tb = new Date(b?.createdAt || 0).getTime();
    return tb - ta;
  });

  for (const fb of sortedFeedbacks) {
    // After a coach Move to another exercise, ignore this clip on the old TE
    // even if the filename still embeds the old _kot_{matchId}_ fragment.
    if (
      preferredTeId &&
      fb?.te_id != null &&
      String(fb.te_id).trim() &&
      String(fb.te_id) !== preferredTeId
    ) {
      continue;
    }

    const url = fb?.video_message_url;
    if (!url) continue;
    const absolute = toAbsoluteMediaUrl(url, baseUrl);
    if (!isPlayableKotMatchVideoUrl(absolute)) continue;

    const haystack = String(url).toLowerCase();
    const text = String(fb.text_message || "");
    const messageType = String(fb.message_type || "").toLowerCase();
    const isVideoFeedback =
      messageType === "video" || messageType === "evaluation";
    if (!isVideoFeedback) continue;

    const parsedMatchId = parseKotMatchIdFromFeedbackText(text);

    // Explicit kot_match_id tag is authoritative after a coach Move.
    if (parsedMatchId) {
      if (matchIdSet.has(parsedMatchId) && !map.has(parsedMatchId)) {
        map.set(parsedMatchId, absolute);
      }
      continue;
    }

    for (const { id, frag } of fragments) {
      if (map.has(id)) continue;
      const needle = `_kot_${frag}_`;
      if (haystack.includes(needle)) {
        map.set(id, absolute);
      }
    }
  }

  return map;
}

export function attachVideoUrlsToKotRows(
  rows,
  videoUrlByMatchId,
  kotMatchesFromApi = [],
  baseUrl = null,
  feedbacks = []
) {
  if (!rows?.length) return [];
  const apiUrlById = new Map();
  for (const m of kotMatchesFromApi || []) {
    const id = String(m?.kot_match_id || "");
    const url = m?.video_url;
    if (id && url) apiUrlById.set(id, url);
  }

  const messageIdByUrl = new Map();
  const viewSetupByUrl = new Map();
  for (const fb of feedbacks || []) {
    const abs = toAbsoluteMediaUrl(fb?.video_message_url, baseUrl);
    if (!abs || !fb?.message_id) continue;
    if (!messageIdByUrl.has(abs)) messageIdByUrl.set(abs, fb.message_id);
    if (fb.view_setup != null && !viewSetupByUrl.has(abs)) {
      viewSetupByUrl.set(abs, fb.view_setup);
    }
  }

  return rows.map((row) => {
    const id = String(row.kot_match_id);
    const linkedFb = findFeedbackForKotMatch(feedbacks, id);
    const fromApi = apiUrlById.get(id);
    const fromMap = videoUrlByMatchId?.get?.(id);
    // Prefer the feedback URL when we have a DB row — same asset the player
    // saves view_setup against (API match video_url can differ slightly).
    const raw =
      linkedFb?.video_message_url || fromApi || fromMap || null;
    const resolved = raw ? toAbsoluteMediaUrl(raw, baseUrl) : null;
    const playable = isPlayableKotMatchVideoUrl(resolved) ? resolved : null;
    const messageId =
      linkedFb?.message_id ||
      (playable ? messageIdByUrl.get(playable) || null : null);
    const viewSetup =
      linkedFb?.view_setup ??
      (playable ? viewSetupByUrl.get(playable) ?? null : null);
    return {
      ...row,
      video_url: playable,
      video_message_id: messageId,
      video_view_setup: viewSetup,
    };
  });
}

function opponentGamePointsFromMatch(match, playerId) {
  const pid = String(playerId);
  const aScore = Number(match.player_a_score);
  const bScore = Number(match.player_b_score);
  if (String(match.player_a_id) === pid && Number.isFinite(bScore)) return bScore;
  if (String(match.player_b_id) === pid && Number.isFinite(aScore)) return aScore;
  return 0;
}

/** Sum of opponent match scores from finished matches. */
export function aggregateOpponentGamePointsFromMatches(playerId, matches = []) {
  if (!playerId || !Array.isArray(matches)) return 0;
  let total = 0;
  for (const match of matches) {
    if (match?.match_state !== "WON" && match?.match_state !== "ABANDONED") {
      continue;
    }
    total += opponentGamePointsFromMatch(match, playerId);
  }
  return total;
}

function aggregateOwnGamePointsFromMatches(playerId, matches = []) {
  if (!playerId || !Array.isArray(matches)) return 0;
  let total = 0;
  for (const match of matches) {
    if (match?.match_state !== "WON" && match?.match_state !== "ABANDONED") {
      continue;
    }
    const pid = String(playerId);
    const aScore = Number(match.player_a_score);
    const bScore = Number(match.player_b_score);
    if (String(match.player_a_id) === pid && Number.isFinite(aScore)) total += aScore;
    else if (String(match.player_b_id) === pid && Number.isFinite(bScore)) total += bScore;
  }
  return total;
}

export function kotLeaderboardGamePointsScored(row, matches = []) {
  const fromRow = Number(row?.game_points_scored);
  if (row?.game_points_scored != null && Number.isFinite(fromRow)) {
    return Math.max(0, fromRow);
  }
  return aggregateOwnGamePointsFromMatches(row?.player_id, matches);
}

export function kotLeaderboardPointSpread(row, matches = []) {
  const scored = kotLeaderboardGamePointsScored(row, matches);
  const conceded = aggregateOpponentGamePointsFromMatches(row?.player_id, matches);
  return scored - conceded;
}

export function sortKotLeaderboardByKotPoints(rows = []) {
  return [...rows].sort((a, b) => {
    const ptsDiff = (Number(b.total_points) || 0) - (Number(a.total_points) || 0);
    if (ptsDiff !== 0) return ptsDiff;
    return (Number(b.matches_won) || 0) - (Number(a.matches_won) || 0);
  });
}

export function sortKotLeaderboardByPointSpread(rows = [], matches = []) {
  return [...rows].sort((a, b) => {
    const spreadDiff =
      kotLeaderboardPointSpread(b, matches) - kotLeaderboardPointSpread(a, matches);
    if (spreadDiff !== 0) return spreadDiff;
    return (
      kotLeaderboardGamePointsScored(b, matches) -
      kotLeaderboardGamePointsScored(a, matches)
    );
  });
}
